import { useEffect, useState } from "react";

import { faUserPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { zodResolver } from "@hookform/resolvers/zod";
import { Player } from "@lottiefiles/react-lottie-player";
import { Stack, TextField, Typography } from "@mui/material";
import axios from "axios";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { CountryData } from "react-phone-input-2";
import { useQueryClient } from "react-query";
import invariant from "ts-invariant";

import { useGetUsProviderGroupClinics } from "api/services/clinic_partner/hooks";
import { GET_CLINIC_PARTNER_INVITATIONS_QUERY_KEY } from "api/services/clinic_partner/hooks/useGetClinicPartnerInvitations";
import { usePostClinicPartnerInvitation } from "api/services/clinic_partner/hooks/usePostClinicPartnerInvitation";
import {
  type ClinicPartnerInvitationPayload,
  ClinicPartnerInvitationPayloadSchema,
} from "api/services/clinic_partner/schemas/invitations";
import firstConfettiAnimation from "assets/animations/confetti.json";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { MUISelect } from "shared/atoms/MUI";
import { Notification } from "shared/atoms/Notification";
import { PhoneNumberInput } from "shared/atoms/PhoneNumberInput";
import { WidgetBox } from "shared/containers/WidgetBox";

const defaultValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone_number: "",
  phone_country_code: "",
  external_partner_user_id: "",
};

type Status = {
  isError: boolean;
  label: string;
  patientId?: number;
};

export const ClinicPartnerInvitePatient: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { profile } = useProfileContext();
  const [status, setStatus] = useState<Status>({ isError: false, label: "" });
  const [formResetKey, setFormResetKey] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);

  const { data: usProviderGroupClinicsData } = useGetUsProviderGroupClinics();
  const usProviderGroupClinics = usProviderGroupClinicsData?.items ?? [];

  const postClinicPartnerInvitation = usePostClinicPartnerInvitation();

  invariant(profile);

  const form = useForm<ClinicPartnerInvitationPayload>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(ClinicPartnerInvitationPayloadSchema),
    defaultValues,
  });
  const {
    formState: { errors },
  } = form;

  useEffect(() => {
    if (usProviderGroupClinics.length !== 1) return;

    const firstId = usProviderGroupClinics[0]?.id;
    if (firstId && !form.getValues("us_provider_group_clinic_id")) {
      form.setValue("us_provider_group_clinic_id", firstId);
    }
  }, [form, usProviderGroupClinics]);

  const onSubmit = form.handleSubmit(async formData => {
    const payload = formData.email ? formData : { ...formData, email: undefined };
    try {
      await postClinicPartnerInvitation.mutateAsync(
        { payload },
        {
          onSuccess: () => {
            queryClient.invalidateQueries([GET_CLINIC_PARTNER_INVITATIONS_QUERY_KEY]);
            setShowConfetti(true);
            setStatus({ isError: false, label: t("patients.invite_sent") });

            setTimeout(() => {
              setStatus({ isError: false, label: "" });
            }, 10000);

            form.reset({
              ...defaultValues,
              us_provider_group_clinic_id: form.getValues("us_provider_group_clinic_id"),
            });
            setFormResetKey(k => k + 1);
          },
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        switch (error?.response?.data?.error) {
          case "invitation_exists":
            setStatus({ isError: true, label: t("invitations.form.errors.invitation_exists") });
            break;
          case "invalid phone number":
            setStatus({ isError: true, label: t("invitations.form.errors.invalid_phone_number") });
            break;
          default:
            setStatus({ isError: true, label: t("errors.generic_contact_support") });
        }
      } else {
        setStatus({ isError: true, label: t("errors.generic_contact_support") });
        setTimeout(() => setStatus({ isError: false, label: "" }), 3000);
      }
    }
  });

  const defaultCountryCode = profile.market?.toLocaleLowerCase();

  return (
    <WidgetBox
      title={t("invitations.form.title")}
      description={t("invitations.form.description")}
      icon={<FontAwesomeIcon icon={faUserPlus} />}
      color="blue"
    >
      <FormProvider {...form}>
        <form id="quick-sign-up-form" onSubmit={onSubmit}>
          <Stack spacing={1.5} sx={{ backgroundColor: "#fff", borderRadius: 1.5 }}>
            <Typography variant="body1">{t("invitations.form.patient_details")}</Typography>
            <Stack direction="row" spacing={2}>
              <Controller
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    label={t("form.first_name")}
                    error={Boolean(errors.first_name)}
                    helperText={Boolean(errors.first_name) && t("errors.field.required")}
                    fullWidth
                  />
                )}
              />
              <Controller
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="text"
                    label={t("form.last_name")}
                    error={Boolean(errors.last_name)}
                    helperText={Boolean(errors.last_name) && t("errors.field.required")}
                    fullWidth
                  />
                )}
              />
            </Stack>

            <Controller
              control={form.control}
              name="email"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="email"
                  label={t("form.email")}
                  error={Boolean(errors.email)}
                  helperText={Boolean(errors.email) && t("errors.field.required")}
                />
              )}
            />

            <Controller
              key={formResetKey}
              name="phone_number"
              control={form.control}
              render={({ field: { value, onChange } }) => (
                <PhoneNumberInput
                  country={defaultCountryCode}
                  value={value}
                  onChange={(inputValue, data: CountryData, event) => {
                    event.stopPropagation();
                    form.setValue("phone_country_code", data.countryCode.toLocaleUpperCase());
                    onChange(inputValue);
                  }}
                  error={Boolean(errors.phone_number)}
                  helperText={errors.phone_number ? t("errors.field.required") : undefined}
                />
              )}
            />

            {usProviderGroupClinics.length > 1 && (
              <Controller
                control={form.control}
                name="us_provider_group_clinic_id"
                render={({ field: { ref, ...field } }) => (
                  <MUISelect
                    {...field}
                    label={t("invitations.form.clinic")}
                    options={usProviderGroupClinics.map(clinic => ({ label: clinic.name, value: clinic.id }))}
                    value={field.value ?? ""}
                    inputRef={ref}
                    error={Boolean(errors.us_provider_group_clinic_id)}
                    formHelperText={errors.us_provider_group_clinic_id ? t("errors.field.required") : undefined}
                  />
                )}
              />
            )}

            <Controller
              control={form.control}
              name="external_partner_user_id"
              render={({ field }) => (
                <TextField
                  {...field}
                  type="text"
                  label={t("form.external_id")}
                  error={Boolean(errors.external_partner_user_id)}
                  helperText={Boolean(errors.external_partner_user_id) && t("errors.field.required")}
                  fullWidth
                />
              )}
            />

            <BaseButton
              type="submit"
              text={t("invitations.form.submit")}
              disabled={postClinicPartnerInvitation.isLoading}
              loading={postClinicPartnerInvitation.isLoading}
            />

            {status.label && (
              <Notification variant={status.isError ? "danger" : "success"} style={{ zIndex: 1000 }}>
                <Stack data-testid={status.isError ? "invite-error" : "invite-success"}>
                  <Typography variant="body2">{status.label}</Typography>
                </Stack>
              </Notification>
            )}
          </Stack>
        </form>
      </FormProvider>

      {showConfetti && (
        <Player
          autoplay
          data-testid="confetti-animation"
          src={firstConfettiAnimation}
          onEvent={event => {
            if (event === "complete") {
              setShowConfetti(false);
            }
          }}
          style={{ position: "absolute", left: 0, right: 0, bottom: 0 }}
        />
      )}
    </WidgetBox>
  );
};
